import React from 'react';
import { CircleCheckIcon, CircleAlertIcon, CircleSlashIcon, ShieldCheckIcon } from 'lucide-react';
import { Band, PullQuote } from './Band';
import { decisionSummary, decisionSummaryException, defectResults, reconstruction, reconstructionAttestation } from '../../data/site';
const OUTCOME_STYLES: Record<string, {
  icon: typeof CircleCheckIcon;
  className: string;
}> = {
  'Handled · resolved correctly': {
    icon: CircleCheckIcon,
    className: 'text-teal'
  },
  'Handled · appropriately escalated': {
    icon: CircleAlertIcon,
    className: 'text-teal'
  },
  'Verified proportionately': {
    icon: ShieldCheckIcon,
    className: 'text-teal'
  },
  Missed: {
    icon: CircleSlashIcon,
    className: 'text-clay'
  }
};
const callouts = [{
  title: 'The exception leads',
  body: 'The material failure sits at the top of the record, not buried at the end of a summary.'
}, {
  title: 'One moment, reconstructed',
  body: 'The missed scenario is replayed field by field: what the AI said, what evidence existed, what was opened, and what the candidate would have signed off.'
}, {
  title: 'A named human decides',
  body: 'Lentic describes. The reviewer grades, on the evidence rather than the summary, and signs.'
}];
const reviewerFields = ['Material concerns', 'Evidence relied on', 'Reviewer', 'Date'];
const FIELD_LABEL = 'font-mono text-[11px] uppercase tracking-[0.1em] text-graphite';
const SECTION_LABEL = 'font-mono text-xs uppercase tracking-[0.14em] text-teal';
export function DecisionRecord() {
  return <Band id="record" eyebrow="What you get back" title="The hiring decision comes with its evidence." description="Every Lentic assessment produces a Decision Record showing what the candidate was asked to do, what the AI produced, what the candidate verified, what they missed, and what a human ultimately decided." aside={<PullQuote data-id="element-73" data-mp="19gufmzvmpsk9">
          A score tells you who ranked higher. The Decision Record tells you how
          each person exercised judgement when AI was involved.
        </PullQuote>} data-id="element-72" data-mp="qpbdswdcckjt">
      <p className="mb-10 font-display text-2xl font-light leading-snug text-ink" data-id="element-74" data-mp="b4i5d2cw6ee0">
        Not a score. Not a percentile. A reconstructable record of judgement.
      </p>

      <div className="grid gap-10 lg:grid-cols-[1.55fr_0.45fr] lg:items-start" data-id="element-75" data-mp="g1rwqnjud04h">
        <article className="overflow-hidden rounded-2xl border border-mist bg-white shadow-[0_30px_70px_-50px_rgba(14,36,54,0.6)]" data-id="element-76" data-mp="kvw1r19jqy2x">
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-mist px-7 py-5" data-id="element-77" data-mp="usqwra6m2gcg">
            <div data-id="element-78" data-mp="yum16nju99ug">
              <p className="font-mono text-xs uppercase tracking-[0.14em] text-graphite" data-id="element-79" data-mp="yu427j5skhiu">
                Decision record · candidate 0417
              </p>
              <h3 className="mt-1.5 font-display text-2xl text-ink" data-id="element-80" data-mp="13r2xw653swj5">
                Financial Controller · Round 1
              </h3>
            </div>
            <p className="font-mono text-xs text-graphite" data-id="element-81" data-mp="35llf19ulqbti">
              library v2026.03.4 · 6 scenarios
            </p>
          </header>

          {/* Decision summary — machine-generated description */}
          <div className="border-b border-mist px-7 py-6" data-id="element-82" data-mp="2t8ztw72im0t4">
            <h4 className={SECTION_LABEL} data-id="element-83" data-mp="2oayb7n9043i6">Decision summary</h4>
            <p className="mt-4 max-w-[76ch] border-l-2 border-clay pl-5 text-[17px] font-medium leading-relaxed text-ink" data-id="element-84" data-mp="2o9evpyv65whg">
              {decisionSummaryException}
            </p>
            <p className="mt-4 max-w-[76ch] text-[17px] leading-relaxed text-graphite" data-id="element-85" data-mp="3gzn634jgrclh">
              {decisionSummary}
            </p>
          </div>

          <div className="px-7 py-6" data-id="element-86" data-mp="371pk6np2trw1">
            <h4 className={SECTION_LABEL} data-id="element-87" data-mp="370o8ch08l1d9">Scenario schedule · 6 of 6</h4>
            <p className="sr-only" data-id="element-88" data-mp="3315kpdh16c6q">
              Six scenarios, with the missed one highlighted and reconstructed
              below.
            </p>
            <ul className="mt-4 divide-y divide-mist/70" data-id="element-89" data-mp="1vkpqpoqzt70f">
              {defectResults.map(result => {
              const style = OUTCOME_STYLES[result.outcome];
              const Icon = style.icon;
              const missed = result.outcome === 'Missed';
              return <li key={result.id} className={`flex gap-4 py-4 ${missed ? '-mx-4 rounded-lg bg-clay/[0.06] px-4' : ''}`} data-id="element-90" data-mp="3kaq01qw2hdyz">
                    <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.className}`} aria-hidden="true" data-id="element-91" data-mp="23a0wun2j155" />
                    <div className="flex-1" data-id="element-92" data-mp="700r4asl7d4a">
                      <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-93" data-mp="3p89h0yk5g4um">
                        <span className="flex flex-wrap items-baseline gap-3" data-id="element-94" data-mp="3t8s82qe7x6vr">
                          <span className="font-mono text-sm text-ink" data-id="element-95" data-mp="2dskq5w3ryloj">{result.id}</span>
                          <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted" data-id="element-96" data-mp="2duxin375sefu">
                            {result.type}
                          </span>
                        </span>
                        <span className={`text-sm font-semibold ${style.className}`} data-id="element-97" data-mp="25bke7oj9gwh4">
                          {result.outcome} · {result.credit}
                        </span>
                      </div>
                      <p className="mt-1.5 text-[15px] leading-relaxed text-graphite" data-id="element-98" data-mp="2f9zvkzshkrdf">
                        {result.detail}
                      </p>
                      {result.reconstructed ? <p className="mt-2 font-mono text-[11px] text-clay" data-id="element-99" data-mp="1mlb4zup244fx">
                          → reconstructed below
                        </p> : null}
                    </div>
                  </li>;
            })}
            </ul>
          </div>

          {/* Reconstruction */}
          <div className="border-t border-mist bg-bone/60 px-7 py-6" data-id="element-100" data-mp="18smiuk9fw4vd">
            <h4 className={SECTION_LABEL} data-id="element-101" data-mp="13utbvp0bb6u1">Reconstruction · 00:14:31</h4>
            <dl className="mt-4 divide-y divide-mist/70" data-id="element-102" data-mp="13sgxtmxodgm9">
              {reconstruction.map(row => <div key={row.field} className="grid gap-1 py-3 sm:grid-cols-[16rem_1fr] sm:gap-6" data-id="element-103" data-mp="1tgw3q94rt669">
                  <dt className={FIELD_LABEL} data-id="element-104" data-mp="22c260y4u1641">{row.field}</dt>
                  <dd className={`text-[15px] leading-snug ${row.value === 'Missed' || row.value === 'Incorrect' ? 'font-semibold text-clay' : 'text-ink'}`} data-id="element-105" data-mp="279u6igjjm0ju">
                    {row.value}
                  </dd>
                </div>)}
            </dl>
            <p className="mt-5 max-w-[70ch] text-sm leading-relaxed text-muted" data-id="element-106" data-mp="1ojcfqhi9tf14">
              Confidence is recorded at submission, not during the work, so that
              being asked does not itself prompt the verification.
            </p>

            <dl className="mt-4 rounded-xl border border-mist bg-white px-5 py-4" data-id="element-107" data-mp="2zxhk3bmebqwz">
              {reconstructionAttestation.map(row => <div key={row.field} className="grid gap-1 py-2 sm:grid-cols-[14rem_1fr] sm:gap-6" data-id="element-108" data-mp="jccl3mnw7axe">
                  <dt className={FIELD_LABEL} data-id="element-109" data-mp="ekbby9m0u0bl">{row.field}</dt>
                  <dd className={`text-[15px] leading-snug ${row.adverse ? 'font-semibold text-clay' : 'text-ink'}`} data-id="element-110" data-mp="9mt4knnnjt5p">
                    {row.value}
                  </dd>
                </div>)}
            </dl>
          </div>

          {/* Human reviewer conclusion */}
          <div className="border-t border-mist bg-bone px-7 py-7" data-id="element-111" data-mp="3ru8ssov44lkr">
            <h4 className={SECTION_LABEL} data-id="element-112" data-mp="3mucun693jod4">Human reviewer conclusion</h4>

            <div className="mt-5 grid gap-1 sm:grid-cols-[16rem_1fr] sm:gap-6" data-id="element-113" data-mp="1q11cpd7odzxt">
              <span className={FIELD_LABEL} data-id="element-114" data-mp="1l1y63oc1rlnq">Overall evidence</span>
              <div className="flex flex-wrap gap-6" data-id="element-115" data-mp="1kzlqvixobped">
                {['Strong', 'Mixed', 'Limited'].map(option => <span key={option} className="flex items-center gap-2.5 text-[15px] text-ink" data-id="element-116" data-mp="yvfh7n1eirvl">
                    <span className="h-3.5 w-3.5 border border-ink/40 bg-white" aria-hidden="true" data-id="element-117" data-mp="l40o2nds5aat" />
                    {option}
                  </span>)}
              </div>
            </div>

            <dl className="mt-5 divide-y divide-mist" data-id="element-118" data-mp="b64e3ywkwlea">
              {reviewerFields.map(field => <div key={field} className="grid items-baseline gap-1 py-3.5 sm:grid-cols-[16rem_1fr] sm:gap-6" data-id="element-119" data-mp="g4nyhuvixp4j">
                  <dt className={FIELD_LABEL} data-id="element-120" data-mp="279a8th0bne5">{field}</dt>
                  <dd className="h-5 border-b border-dashed border-ink/25" data-id="element-121" data-mp="67t33kujs4uh" />
                </div>)}
            </dl>

            <p className="mt-6 rounded-xl border border-ink/20 bg-white px-5 py-4 text-[15px] leading-relaxed text-ink" data-id="element-122" data-mp="1wjz4xdf790ml">
              <span className="font-semibold" data-id="element-123" data-mp="3l2tg5v6ev43k">Human review required.</span> No
              decision is recorded until a named reviewer has examined the
              underlying evidence — not this summary — and signed off.
            </p>
          </div>

          <footer className="border-t border-mist px-7 py-5" data-id="element-124" data-mp="3uteo497z5kz5">
            <p className="text-sm leading-relaxed text-muted" data-id="element-125" data-mp="3q1eic0583xun">
              Condensed view. The full record also carries observed behaviour,
              the candidate's own reflection and the comparability statement.
            </p>
          </footer>
        </article>

        <aside className="space-y-4 lg:sticky lg:top-24" data-id="element-126" data-mp="3ooazjurx4wp">
          {callouts.map(callout => <div key={callout.title} className="border-l-2 border-teal pl-5" data-id="element-127" data-mp="hjqpuwhlm4ln">
              <h3 className="font-display text-xl font-medium text-ink" data-id="element-128" data-mp="cnt8cmulms7a">
                {callout.title}
              </h3>
              <p className="mt-2 max-w-[34ch] text-[15px] leading-relaxed text-graphite" data-id="element-129" data-mp="2eeegvyv5ayc0">
                {callout.body}
              </p>
            </div>)}
        </aside>
      </div>

      <aside className="mt-6 rounded-2xl border border-clay/30 bg-clay/[0.05] p-8 lg:p-10" data-id="element-130" data-mp="1hkro17rmfr76">
        <h3 className="font-display text-2xl font-medium text-clay" data-id="element-131" data-mp="1cqdyhlzn4eaf">
          The record you need when a hiring decision is challenged
        </h3>
        <p className="mt-4 max-w-[70ch] text-lg leading-relaxed text-ink" data-id="element-132" data-mp="13oegaqcw345w">
          Most hiring records explain who was hired. Lentic preserves the
          evidence behind the decision, including the candidates you chose not
          to advance.
        </p>
      </aside>
    </Band>;
}